import type { ReplayGameSummary } from '../../replay/replayApi';
import type {
  OpeningSearchResult,
  OpeningVariation,
  PlayerOpeningStat,
} from '../browse/gamesApi';
import { variationRowKey } from '../browse/openingVariationBranch';

/** Text before the first colon, e.g. "Caro-Kann Defense" from "Caro-Kann Defense: Classical". */
export const getOpeningFamilyName = (opening: string): string => {
  const trimmed = opening.trim();
  const colonIndex = trimmed.indexOf(':');
  return colonIndex >= 0 ? trimmed.slice(0, colonIndex).trim() : trimmed;
};

/** Prefer "Sicilian Defense: Najdorf" over "Sicilian Defense: Najdorf Variation(s)". */
export const formatOpeningName = (opening: string): string =>
  opening.replace(/\s+Variations?/gi, '').trim();

const compactFamilySuffixPattern = /\s+(Defense|Opening)$/i;

const compactOpeningFamilyName = (family: string): string =>
  formatOpeningName(family).replace(compactFamilySuffixPattern, '').trim();

/** Compact board header label, e.g. "Caro-Kann: Tartakower". */
export const formatCompactOpeningName = (opening: string): string => {
  const trimmed = opening.trim();
  if (!trimmed) {
    return trimmed;
  }

  const colonIndex = trimmed.indexOf(':');
  if (colonIndex < 0) {
    return compactOpeningFamilyName(trimmed);
  }

  const family = compactOpeningFamilyName(trimmed.slice(0, colonIndex));
  const variation = formatOpeningName(trimmed.slice(colonIndex + 1).trim());
  return variation ? `${family}: ${variation}` : family;
};

/** Top-level browse labels: compact opening family name only. */
export const formatOpeningBrowseLabel = (
  opening: Pick<PlayerOpeningStat | OpeningSearchResult, 'opening'>,
): string => formatCompactOpeningName(getOpeningFamilyName(opening.opening));

type OpeningEcoFields = Pick<
  PlayerOpeningStat | OpeningSearchResult,
  'opening' | 'eco'
>;

/** Drilldown labels: compact opening name without ECO. */
export const formatOpeningLabel = (opening: OpeningEcoFields): string =>
  formatCompactOpeningName(opening.opening);

export type OpeningVariationLabelParts = {
  eco?: string;
  name: string;
  /** Full reference name for tooltips. */
  fullName: string;
};

/** Compact variation name plus full reference for tooltips. */
export const getOpeningVariationLabelParts = (
  variation: OpeningEcoFields & { lichessOpening?: string },
): OpeningVariationLabelParts => {
  const fullName = (variation.lichessOpening ?? variation.opening).trim();
  const eco = variation.eco?.trim();
  const name = formatCompactOpeningName(fullName);
  return eco ? { eco, name, fullName } : { name, fullName };
};

/** Compact opening variation label without ECO. */
export const formatOpeningVariationLabel = (
  variation: OpeningEcoFields & { lichessOpening?: string },
): string => getOpeningVariationLabelParts(variation).name;

export const buildVariationNameByKey = (
  variations: Array<Pick<OpeningVariation, 'opening' | 'eco' | 'lichessOpening'>>,
): Map<string, string> => {
  const map = new Map<string, string>();
  for (const variation of variations) {
    const name = variation.lichessOpening?.trim();
    if (name) {
      map.set(variationRowKey(variation), name);
    }
  }
  return map;
};

/** Prefer the lichess variation name over the corpus family name when known. */
export const enrichGameWithVariationOpening = (
  game: ReplayGameSummary,
  variationNameByKey: Map<string, string>,
  selectedVariation?: Pick<
    OpeningVariation,
    'opening' | 'eco' | 'lichessOpening'
  > | null,
): ReplayGameSummary => {
  if (game.lichessOpening?.trim()) {
    return game;
  }

  const lichessOpening =
    variationNameByKey.get(
      variationRowKey({
        opening: game.opening ?? '',
        eco: game.eco,
      }),
    ) ??
    (selectedVariation?.eco === game.eco
      ? selectedVariation?.lichessOpening?.trim()
      : undefined);

  if (!lichessOpening) {
    return game;
  }

  return { ...game, lichessOpening };
};
